import { useState, useEffect } from 'react'

import { useUmgebung } from '../../hooks/queries'
import type { Umgebungstreffer } from '../../api/types'
import { entfernungText, meterZwischen } from '../../lib/entfernung'
import {
  BergIcon,
  BesteckIcon,
  BettIcon,
  GlasIcon,
  SaeulenIcon,
  SternIcon,
  TascheIcon,
  XIcon,
  ZeltIcon,
} from '../../components/Icons'

/**
 * Die acht Schubladen — hier nur Name und Zeichen.
 *
 * **Was Apple darunter versteht, steht auf dem Server** (`Umgebung.php`):
 * Suchwort und Kategorien. Von hier geht nur die Marke hinaus. Das ist kein
 * Formalismus — es hält den fremden Dienst an einer Stelle, und es hält den
 * Browser davon ab, einen Suchbegriff frei zu bilden.
 *
 * Es ist bewusst **nicht** Apples eigene Reihe. Dort stehen Tankstellen und
 * Parkhäuser, weil sie zur Navigation gehören; in einem Reisebericht erzählt
 * niemand vom Parkhaus. **Camping steht dagegen für sich** und nicht bei
 * „Übernachtung" mit drin: unter dem Suchwort „Hotel" findet Apple kein
 * „Aire de camping-car".
 */
export const KACHELN: { key: string; titel: string; Zeichen: typeof BesteckIcon }[] = [
  { key: 'essen', titel: 'Essen & Trinken', Zeichen: BesteckIcon },
  { key: 'abend', titel: 'Bar & Abend', Zeichen: GlasIcon },
  { key: 'kultur', titel: 'Kultur', Zeichen: SaeulenIcon },
  { key: 'sehenswert', titel: 'Sehenswürdigkeit', Zeichen: SternIcon },
  { key: 'natur', titel: 'Natur & Aussicht', Zeichen: BergIcon },
  { key: 'uebernachten', titel: 'Übernachtung', Zeichen: BettIcon },
  { key: 'camping', titel: 'Camping & Stellplatz', Zeichen: ZeltIcon },
  { key: 'laden', titel: 'Markt & Laden', Zeichen: TascheIcon },
]

/**
 * In der Nähe finden — und den Punkt genau dorthin setzen.
 *
 * **Wofür das gut ist:** „49.78940, 6.67869" ist ein Koordinatenpaar, „Cave du
 * Cap, Le Lavandou" ist eine Erinnerung. Ein Highlight soll sagen können, in
 * *welchem* Restaurant und an *welchem* Aussichtspunkt jemand war.
 *
 * **Der Server fragt, nicht dieser Browser.** Nach außen geht die Adresse des
 * Servers und der Punkt — keine IP eines Lesenden, keine Namen, keine Fotos.
 * Dieselbe Ausnahme wie bei Ortsname und Wetter, derselbe Dienst; ein dritter
 * käme nicht dazu. Anders als dort geschieht es aber **auf Knopfdruck** und
 * damit öfter als einmal je Highlight — ausgelöst nur vom Reisenden.
 *
 * **Nichts wird gespeichert.** Was hier steht, ist eine Auskunft; erst das
 * Antippen setzt Titel und Punkt im Formular, und erst „Sichern" schreibt.
 */
export function Umgebungswahl({
  tripId,
  lat,
  lon,
  onGewaehlt,
  onSchliessen,
  weit,
  titel,
}: {
  tripId: number
  /** Der Punkt, um den gesucht wird — die Mitte, nicht das Ergebnis. */
  lat: number
  lon: number
  onGewaehlt: (treffer: Umgebungstreffer) => void
  onSchliessen: () => void
  /**
   * Der **zweite, größere** Umkreis aus den Einstellungen.
   *
   * Zwei Radien, zwei Fragen: der enge beantwortet „in *welchem* Lokal war
   * ich?", der weite „was war an dem Tag noch da?". Gesetzt wird er dort, wo
   * weitere Orte an einen Punkt kommen.
   */
  weit?: boolean
  /** Die Überschrift — je nachdem, wonach gerade gesucht wird. */
  titel?: string
}) {
  const suche = useUmgebung(tripId)
  const [kachel, setKachel] = useState<string | null>(null)
  /** Was im Feld steht, und wonach zuletzt gesucht wurde. */
  const [wort, setWort] = useState('')
  const [gesucht, setGesucht] = useState<string | null>(null)
  const [treffer, setTreffer] = useState<Umgebungstreffer[] | null>(null)
  /**
   * Was der Server über den Abruf sagt — Status und eine Zeile dazu.
   *
   * Steht neben den Treffern und nicht in ihnen: eine leere Liste ist noch
   * keine Auskunft, und genau daran ist im Betrieb eine Suche mitten in
   * Manhattan gescheitert, ohne dass jemand den Grund sehen konnte.
   */
  const [befund, setBefund] = useState<{ status: number; diagnose: string } | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  /**
   * Eine Schublade **oder** ein getipptes Wort — nie beides.
   *
   * **Warum es das Feld überhaupt gibt:** Apple führt keine Kategorie für
   * Pizzeria, Imbiss oder die Küche einer Weltgegend. Essbar ist
   * `Restaurant`, `Cafe`, `Bakery`, und damit endet die Liste — die acht
   * Kacheln sind also nicht eine Auswahl aus vielen, sondern schon alles, was
   * es gibt. Wer ein bestimmtes Lokal meint, kommt nur über seinen Namen hin.
   */
  async function suchen(was: { kind?: string; q?: string }) {
    setKachel(was.kind ?? null)
    setGesucht(was.q ?? null)
    setTreffer(null)
    setBefund(null)
    setProblem(null)
    try {
      const antwort = await suche.mutateAsync({ ...was, lat, lon, weit })
      setBefund({ status: antwort.status, diagnose: antwort.diagnose })
      // **Das Nächste zuerst.** Der Dienst sortiert nach seiner eigenen
      // Vorstellung von Wichtigkeit; hier zählt, wo jemand tatsächlich stand.
      setTreffer(
        [...antwort.results].sort(
          (a, b) =>
            meterZwischen(lat, lon, a.lat, a.lon) - meterZwischen(lat, lon, b.lat, b.lon),
        ),
      )
    } catch (fehler) {
      setProblem(
        fehler instanceof Error ? fehler.message : 'Die Suche hat nicht geklappt.',
      )
    }
  }

  const gewaehlteKachel = KACHELN.find((k) => k.key === kachel) ?? null

  // Escape schliesst -- wie die Grossansicht der Fotos. Ein Blatt, das nur
  // auf das X hoert, ist am Rechner eine Sackgasse.
  useEffect(() => {
    const taste = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onSchliessen()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [onSchliessen])

  return (
    <div className="bogen" role="dialog" aria-modal="true" aria-label={titel ?? 'In der Nähe finden'}>
      <div className="bogen__blatt">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{titel ?? 'In der Nähe finden'}</h3>
          <button
            type="button"
            className="icon-button"
            onClick={onSchliessen}
            aria-label="Schließen"
            title="Schließen"
          >
            <XIcon size={18} />
          </button>
        </div>

        {gewaehlteKachel === null && gesucht === null ? (
          <>
            <p className="bogen__hinweis">
              Gesucht wird rund um den Punkt, der gerade im Formular steht. Wer
              etwas auswählt, setzt Titel und Nadel genau dorthin.
            </p>
            <div className="umgebung">
              {KACHELN.map(({ key, titel, Zeichen }) => (
                <button
                  key={key}
                  type="button"
                  className="umgebung__kachel"
                  onClick={() => void suchen({ kind: key })}
                >
                  <Zeichen size={20} />
                  <span>{titel}</span>
                </button>
              ))}
            </div>

            {/* **Ein freies Feld neben den acht Schubladen.**

                Aus dem Betrieb: „in Karten gibt es eine Kategorie Pizzeria,
                können wir die zu Essen hinzufügen." Können wir nicht — Apples
                Kategorienliste kennt sie nicht, und die acht Kacheln sind
                schon alles, was es an essbaren Schubladen gibt. Was hilft, ist
                der Name.

                **Und das ist ein freies Suchfeld**, also dieselbe Ausnahme wie
                bei „Ort suchen" auf der Karte: nur der Reisende löst sie aus,
                der Server fragt, und nur auf Knopfdruck. Der Satz darunter
                sagt es, statt es zu verschweigen. */}
            <form
              className="umgebung__suche"
              onSubmit={(event) => {
                event.preventDefault()
                const begriff = wort.trim()
                if (begriff === '') return
                void suchen({ q: begriff })
              }}
            >
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="field__label">Oder nach dem Namen suchen</span>
                <input
                  className="field__input"
                  type="search"
                  maxLength={120}
                  placeholder={'Pizzeria, Nidarosdom, „60 seconds to napoli“ …'}
                  value={wort}
                  onChange={(event) => setWort(event.target.value)}
                />
              </label>
              <button
                type="submit"
                className="button button--small"
                disabled={wort.trim() === ''}
              >
                Suchen
              </button>
              <p className="bogen__hinweis">
                Gesucht wird über den Server bei Apple Maps, im selben Umkreis
                wie die Kacheln.
              </p>
            </form>
          </>
        ) : (
          <>
            {/* Zurück zur Auswahl, ohne den Dialog zu verlassen: die erste
                Schublade ist oft nicht die richtige. */}
            <div className="bogen__vorschlag">
              <button
                type="button"
                className="button button--outline button--small"
                onClick={() => {
                  setKachel(null)
                  setGesucht(null)
                  setTreffer(null)
                  setProblem(null)
                }}
              >
                Andere Suche
              </button>
              <span className="bogen__hinweis">
                {gewaehlteKachel?.titel ?? `„${gesucht ?? ''}"`}
              </span>
            </div>

            {suche.isPending ? (
              <p className="bogen__hinweis">Wird gesucht …</p>
            ) : problem !== null ? (
              <p className="bogen__problem" role="alert">{problem}</p>
            ) : treffer !== null && treffer.length === 0 ? (
              // **Leer ist ein Ergebnis, kein Fehler -- aber nicht jede leere
              // Liste ist eines.** Hat Apple abgelehnt, wäre „nichts gefunden"
              // schlicht gelogen; der Status sagt den Unterschied. Und darunter
              // steht, was gefragt wurde und was zurückkam: dieselbe Zeile wie
              // beim Videobefund, zum Suchen und nicht zum Ansehen.
              <>
                <p className="bogen__hinweis">
                  {befund !== null && befund.status !== 200
                    ? 'Die Karte hat nicht geantwortet. Später noch einmal versuchen — am Punkt selbst liegt es nicht.'
                    : 'Hier hat die Karte nichts gefunden. Eine andere Kategorie hilft vielleicht — oder ein größerer Umkreis in den Einstellungen.'}
                </p>
                {befund !== null && befund.diagnose !== '' && (
                  <p className="bogen__befund">{befund.diagnose}</p>
                )}
              </>
            ) : (
              <>
              <div className="umgebung">
                {(treffer ?? []).map((t, i) => (
                  <button
                    key={`${t.name}-${i}`}
                    type="button"
                    className="umgebung__treffer"
                    // **Die Schublade geht mit.** Apple sagt sie je Treffer;
                    // fehlt sie, gilt die Kachel, nach der gesucht wurde --
                    // wer unter „Kultur" sucht, hat ein kulturelles Ziel
                    // gewählt, auch wenn Apple die Kategorie nicht führt.
                    onClick={() => onGewaehlt({ ...t, marke: t.marke !== '' ? t.marke : (kachel ?? '') })}
                  >
                    <span className="umgebung__name">{t.name}</span>
                    <span className="umgebung__zeile">
                      {entfernungText(meterZwischen(lat, lon, t.lat, t.lon))}
                      {t.ort !== '' && ` · ${t.ort}`}
                    </span>
                    {t.adresse !== '' && (
                      <span className="umgebung__zeile umgebung__zeile--leise">
                        {t.adresse}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {/* **Die Befundzeile steht jetzt auch unter einer vollen Liste.**

                  Aus dem Betrieb: „Ich bin hier — 60 seconds to napoli. Wird
                  auf der Apple Karte angezeigt. Aber nicht in unserer
                  Suchauswahl." Fünf Treffer standen da, der eine fehlte — und
                  woran es lag, war nirgends abzulesen: die Zeile erschien nur,
                  wenn gar nichts kam.

                  Dabei steht genau dort die Antwort. „5 von 10 · 5 zu weit"
                  heißt: Apple hat seine zehn geliefert, die Hälfte lag außerhalb
                  des Umkreises — dann ist die Liste voll, bevor die Nachbarschaft
                  drankommt, und keine Kategorie der Welt hilft. „5 von 5" heißt
                  das Gegenteil: Apple hatte nicht mehr. */}
              {befund !== null && befund.diagnose !== '' && (
                <p className="bogen__befund">{befund.diagnose}</p>
              )}
              </>
            )}
          </>
        )}

        <div className="bogen__fuss">
          <button
            type="button"
            className="button button--outline button--small"
            onClick={onSchliessen}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Das Zeichen zu einer Marke — oder `null`.
 *
 * **Für den Kartenstapel**, wo ein aus der Karte gewählter Punkt sein Zeichen
 * vor dem Titel trägt: Besteck beim Restaurant, Säulen beim Museum, Zelt beim
 * Stellplatz. `null` heißt: kein Zeichen, weil die Marke fehlt oder aus einer
 * Fassung stammt, die diese Liste nicht kennt — besser, als eine Schublade
 * zu raten.
 */
export function zeichenFuer(marke: string): typeof BesteckIcon | null {
  return KACHELN.find((eintrag) => eintrag.key === marke)?.Zeichen ?? null
}
